import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { errorMessage } from "../lib/api";
import { isPasswordStrong } from "../lib/password";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import NotificationChoices from "../components/NotificationChoices";
import TimezoneSelect from "../components/TimezoneSelect";
import { deviceZoneSuggestion, zoneLabel } from "../lib/time";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ShieldCheck, Eye, EyeOff, Mail, Bell, Calendar, MessageCircle } from "lucide-react";

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];

const NOTIFICATION_PREFS = [
  {
    key: "notify_email",
    icon: Mail,
    label: "Notifications par email",
    description: "Recevoir des notifications importantes par email (assignations, rappels, etc.)",
    defaultValue: true,
  },
  {
    key: "notify_task_reminders",
    icon: Bell,
    label: "Rappels de tâches",
    description: "Recevoir un rappel par email pour vos tâches en retard ou à venir",
    defaultValue: true,
  },
  {
    key: "notify_weekly_summary",
    icon: Calendar,
    label: "Résumé hebdomadaire",
    description: "Recevoir chaque semaine un résumé rédigé par le Copilote : vos retards, échéances et dossiers (si l'administrateur l'a activé)",
    defaultValue: false,
  },
  {
    key: "notify_whatsapp",
    icon: MessageCircle,
    label: "Notifications WhatsApp",
    description: "Recevoir vos assignations et rappels de tâches par WhatsApp",
    defaultValue: true,
  },
];

function initialsOf(name, email) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return (parts[0] || email || "?").slice(0, 2).toUpperCase();
}

function formatMemberSince(iso) {
  if (!iso) return "Inconnue";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function formatLastLogin(iso) {
  if (!iso) return "Jamais";
  const d = new Date(iso);
  const date = d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return `${date} à ${time}`;
}

export default function Profile() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [whatsappNumber, setWhatsappNumber] = useState(user?.whatsapp_number || "");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  const [savingPref, setSavingPref] = useState(null);

  const submitName = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Le nom est requis");
      return;
    }
    setSavingName(true);
    try {
      const { data } = await api.patch("/auth/me", { name: trimmed });
      updateUser({ name: data.name });
      const trimmedNumber = whatsappNumber.trim();
      if (trimmedNumber !== (user?.whatsapp_number || "")) {
        const { data: prefsData } = await api.patch("/auth/notifications", { whatsapp_number: trimmedNumber });
        updateUser(prefsData);
        setWhatsappNumber(prefsData.whatsapp_number || "");
      }
      toast.success("Profil mis à jour");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSavingName(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(newPassword)) {
      toast.error(
        "Le mot de passe doit contenir au moins 8 caractères, une minuscule, une majuscule et un chiffre ou caractère spécial"
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSavingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Changing the password revokes every session, including this one, so the
      // current cookies are stale for any later request. Log out locally and send
      // the user back to /login rather than leaving them on a failing page.
      toast.success("Mot de passe modifié. Veuillez vous reconnecter.");
      await logout();
      navigate("/login");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSavingPassword(false);
    }
  };

  const pickAvatar = () => fileInputRef.current?.click();

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!AVATAR_TYPES.includes(file.type)) {
      toast.error("Format d'image non supporté (JPEG, PNG ou WebP uniquement)");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error("L'image ne doit pas dépasser 2 Mo");
      return;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/auth/me/avatar", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Cache-bust client-side only: the stored URL stays stable per user.
      updateUser({ avatar_url: `${data.avatar_url}?t=${Date.now()}` });
      toast.success("Photo de profil mise à jour");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const updatePref = async (key, value) => {
    const previous = user?.[key];
    updateUser({ [key]: value });
    setSavingPref(key);
    try {
      const { data } = await api.patch("/auth/notifications", { [key]: value });
      updateUser(data);
    } catch (e) {
      updateUser({ [key]: previous });
      toast.error(errorMessage(e));
    } finally {
      setSavingPref(null);
    }
  };

  const [savingZone, setSavingZone] = useState(false);
  const saveTimezone = async (zone) => {
    setSavingZone(true);
    try {
      const { data } = await api.patch("/auth/timezone", { timezone: zone });
      updateUser(data);
      toast.success("Fuseau horaire enregistré");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSavingZone(false);
    }
  };
  const zoneSuggestion = user ? deviceZoneSuggestion(user) : null;

  const avatarUrl = user?.avatar_url;
  const initials = initialsOf(user?.name, user?.email);

  return (
    <div className="space-y-4" data-testid="profile-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Mon profil</h1>
        <p className="text-sm text-slate-500">Gérez vos informations personnelles et vos préférences.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="space-y-4">
          {user?.must_change_password && (
            <Card className="p-4 border-amber-200 bg-amber-50/60 flex items-start gap-3">
              <ShieldAlert className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-amber-800">
                <div className="font-medium">Choisissez un nouveau mot de passe</div>
                <div className="text-amber-700/90">
                  Votre compte vient d'être créé avec un mot de passe temporaire. Définissez-en un nouveau ci-dessous pour continuer.
                </div>
              </div>
            </Card>
          )}

          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Informations personnelles</div>
            <form onSubmit={submitName} className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-jamora text-white flex items-center justify-center text-lg font-semibold overflow-hidden flex-shrink-0">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    initials
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={uploadAvatar}
                    data-testid="input-avatar-file"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={pickAvatar}
                    disabled={uploadingAvatar}
                    data-testid="btn-change-avatar"
                  >
                    {uploadingAvatar ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                    Changer la photo
                  </Button>
                  <p className="text-xs text-slate-500 mt-1.5">JPEG, PNG ou WebP. 2 Mo maximum.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Nom complet *</Label>
                  <Input
                    id="profile-name"
                    data-testid="input-profile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-email">Email</Label>
                  <Input id="profile-email" data-testid="input-profile-email" value={user?.email || ""} disabled />
                  <p className="text-xs text-slate-500">L'adresse email ne peut pas être modifiée.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-whatsapp-number">Numéro WhatsApp</Label>
                  <Input
                    id="profile-whatsapp-number"
                    data-testid="input-whatsapp-number"
                    placeholder="+221 77 123 45 67"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                  />
                  <p className="text-xs text-slate-500">Utilisé pour vos notifications WhatsApp.</p>
                </div>
              </div>

              <div className="space-y-2 max-w-xs">
                <Label htmlFor="profile-role">Rôle</Label>
                <Input id="profile-role" data-testid="input-profile-role" value={user?.role_label || ""} disabled />
                <p className="text-xs text-slate-500">Votre rôle est géré par un administrateur.</p>
              </div>

              <Button
                type="submit"
                data-testid="btn-profile-save-name"
                disabled={savingName}
              >
                {savingName ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                Enregistrer
              </Button>
            </form>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Mot de passe</div>
            <form onSubmit={submitPassword} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Mot de passe actuel *</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      data-testid="input-profile-current-password"
                      type={showCurrent ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showCurrent ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                      {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nouveau mot de passe *</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      data-testid="input-profile-new-password"
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showNew ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                      {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe *</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    data-testid="input-profile-confirm-password"
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showConfirm ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  8 caractères minimum, avec une minuscule, une majuscule et un chiffre ou caractère spécial.
                </p>
              </div>
              <Button
                type="submit"
                data-testid="btn-profile-save-password"
                disabled={savingPassword}
              >
                {savingPassword ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                Mettre à jour le mot de passe
              </Button>
            </form>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Préférences de notification</div>
            <div className="divide-y divide-slate-100">
              {NOTIFICATION_PREFS.map(({ key, icon: Icon, label, description, defaultValue, comingSoon }) => (
                <div key={key} className={`flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0 ${comingSoon ? "opacity-50" : ""}`}>
                  <div className="flex items-start gap-3">
                    <Icon size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                        {label}
                        {comingSoon && (
                          <span className="text-2xs font-medium uppercase tracking-wide text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">
                            Bientôt disponible
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{description}</div>
                    </div>
                  </div>
                  <Switch
                    checked={comingSoon ? false : user?.[key] ?? defaultValue}
                    onCheckedChange={(v) => updatePref(key, v)}
                    disabled={comingSoon || savingPref === key}
                    data-testid={`switch-${key}`}
                  />
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5 space-y-3" data-testid="card-timezone">
            <div>
              <div className="font-display font-semibold text-sm text-slate-900">Fuseau horaire</div>
              <div className="text-xs text-slate-500 mt-0.5">Les heures du CRM (réunions, rappels, messages) sont lues et saisies dans ce fuseau.</div>
            </div>
            <label htmlFor="profile-timezone" className="sr-only">Mon fuseau horaire</label>
            <TimezoneSelect id="profile-timezone" value={user?.timezone || ""} onChange={saveTimezone} disabled={savingZone} data-testid="profile-timezone"
              inheritLabel={`Celui de l'entreprise · ${zoneLabel(user?.company_timezone)}`} />
            {zoneSuggestion && (
              <p className="text-xs text-slate-500" data-testid="profile-timezone-device">
                Votre appareil est en heure de {zoneLabel(zoneSuggestion)}.{" "}
                <button type="button" className="underline underline-offset-2 text-jamora" onClick={() => saveTimezone(zoneSuggestion)} data-testid="profile-timezone-use-device">L'utiliser</button>
              </p>
            )}
          </Card>

          <Card className="p-5 space-y-4" data-testid="card-notification-choices">
            <div>
              <div className="font-display font-semibold text-sm text-slate-900">Ce que je reçois</div>
              <div className="text-xs text-slate-500 mt-0.5">Choisissez, par sujet, d'être prévenu dans la cloche du CRM, par WhatsApp, ou les deux.</div>
            </div>
            <NotificationChoices />
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-jamora/10 text-jamora flex items-center justify-center flex-shrink-0 overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-semibold">{initials}</span>
                )}
              </div>
              <div>
                <div className="text-sm font-medium text-slate-900">Votre compte</div>
                <div className="text-xs text-slate-500">Aperçu de votre compte et de vos accès.</div>
              </div>
            </div>
            <dl className="space-y-2.5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">Nom</dt>
                <dd className="text-slate-900 font-medium truncate">{user?.name}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">Email</dt>
                <dd className="text-slate-900 font-medium truncate">{user?.email}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">Rôle</dt>
                <dd className="text-slate-900 font-medium truncate">{user?.role_label}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">Membre depuis</dt>
                <dd className="text-slate-900 font-medium">{formatMemberSince(user?.created_at)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500 flex-shrink-0">Dernière connexion</dt>
                <dd className="text-slate-900 font-medium text-right">{formatLastLogin(user?.last_login)}</dd>
              </div>
            </dl>
          </Card>

          {!user?.must_change_password && (
            <Card className="p-4 bg-emerald-50/60 border-emerald-200 flex gap-3">
              <ShieldCheck size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-emerald-900">Compte sécurisé</div>
                <div className="text-xs text-emerald-700/90 mt-0.5">Votre compte est protégé et à jour.</div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
